'use client';

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/auth-service';
import { LoginRequest, LoginResponse, AuthUser, ApiError } from '@/types';

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: ApiError | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

/**
 * Custom hook for authentication management
 * Provides login, logout, and user state management
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const isAuth = authService.isAuthenticated();

        if (isAuth) {
          // User is authenticated but user data is not stored in localStorage
          // User data will be available after login or refresh token
          // For now, we just know they're authenticated
          setUser(null); // Will be populated on login/refresh
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Failed to initialize auth state:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: LoginResponse = await authService.login(credentials);
      setUser(response.user);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      setUser(null);
      throw err; // Re-throw to allow component-level error handling
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback((): void => {
    authService.logout();
    setUser(null);
    setError(null);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    user,
    isAuthenticated: authService.isAuthenticated(),
    isLoading,
    error,
    login,
    logout,
    clearError,
  };
}
